import React, { useEffect, useState, useCallback } from "react";
import { message } from "antd";
import { useNavigate, useParams } from "react-router-dom";

import PageHeader from "../../components/shared/PageHeader";
import EquipmentForm from "../../components/equipment/EquipmentForm";
import BlueButton from "../../components/shared/BlueButton";
import LoadingModal from "../../components/shared/LoadingModal";

import {
  createEquipment,
  getEquipment,
  updateEquipment,
} from "../../api/equipment";
import { getFileUrl } from "../../api/file";
import { cn, formatDate } from "../../lib/utils";

export default function EquipmentFormView() {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formInstance, setFormInstance] = useState(null);
  const [equipment, setEquipment] = useState(null);

  const { equipmentId } = useParams();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const result = await formInstance?.validateFields();
      const rateData = { rate: result.rate, rateBy: result.rateBy };
      const avatars =
        result.avatars && result.avatars.length > 0
          ? result.avatars.map((file) => {
            if (!file.response) return file;
            return file.response.file;
          })
          : equipment && equipment.avatars;

      const equipmentData = {
        name: result.name,
        nickName: result.nickName,
        make: result.make,
        model: result.model,
        status: result.status,
        type: result.type,
        description: result.description,
        rate: rateData,
        attachments: result.attachments,
        avatars,
      };

      if (editing) {
        await updateEquipment({
          ...equipmentData,
          id: equipmentId,
          rate: rateData,
        }).then(() => {
          message.success("Equipment updated successfully");
        });
      } else {
        await createEquipment({ ...equipmentData, rate: rateData }).then(() => {
          message.success("Equipment created successfully");
        });
      }
      navigate("/equipments");
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getEquipment(equipmentId);
      const activeAttachments = result.attachments && result.attachments.filter(attachment => attachment.isActive);

      const values = {
        ...result,
        rate: result.rate?.rate,
        rateBy: result.rate?.rateBy,
        avatars: result.avatars && result.avatars.map((avatar) => {
          return {
            ...avatar,
            uid: avatar.id,
            name: avatar.originalName,
            status: "done",
            url: getFileUrl(avatar),
          };
        }),

        attachments: result.attachments && activeAttachments.map((attachment) => {
          return {
            ...attachment,
            fileAttachment: {
              ...attachment.fileAttachment,
              file: {
                ...attachment.fileAttachment.file,
                uid: attachment.fileAttachment.id,
                status: "done",
                name: attachment.fileAttachment.file.originalName,
                url: getFileUrl(attachment.fileAttachment.file),
              }
            }
          };
        })
      };
      setEquipment(result);
      formInstance?.setFieldsValue(values);
      if (result.attachments && result.attachments.length > 0) {
        updateDocuments(result)
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setLoading(false);
    }
  }, [equipmentId, formInstance]);

  const updateDocuments = (equipment) => {
    const docs = [];
    const activeDocuments = equipment.attachments.filter(doc => {
      return doc.isActive
    })
    activeDocuments.forEach(
      (doc, index) => {
        docs.push(
          {
            key: `${doc.id + index + 1}`,
            label: "Document Name",
            children: doc.fileAttachment.name
          },
          {
            key: `${doc.id + index + 2}`,
            label: "Expiry Date",
            children: formatDate(doc.fileAttachment.expireAt),
          },
          {
            key: `${doc.id + index + 3}`,
            label: "Document",
            children: doc.fileAttachment.file.name
          }
        );
      }
    );
  }

  useEffect(() => {
    if (equipmentId) {
      setEditing(true);
      fetchEquipment();
    }
  }, [equipmentId, fetchEquipment]);


  return (
    <main className="space-y-6 min-h-full flex flex-col">
      <PageHeader
        title={editing ? "Update Equipment" : "Create Equipment"}
        home="Equipments"
        route="/equipments"
      />

      <div className={cn("bg-white ml-6 p-6 relative")}>
        <div>
          <EquipmentForm
            onFormInstanceReady={(instance) => setFormInstance(instance)}
            formClassName="w-1/2"
          />
        </div>

        <BlueButton type="primary" onClick={handleSubmit}>
          {editing ? "Update" : "Create"} Equipment
        </BlueButton>

        {loading && <LoadingModal />}
      </div>
    </main>
  );
}
